import { useEffect, useState } from "react";
import { marked } from "marked";
import i18n from "i18next";
import { Documents } from "../assets";

export const useDocument = (documentPath: string, customLang?: "en" | "ja") => {
  const [document, setDocument] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState<string>("");

  useEffect(() => {
    const fetchDocument = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // 現在の言語を取得 (カスタム言語が指定されていればそれを使用)
        const lang = customLang || (i18n.language.startsWith("ja") ? "ja" : "en");

        // パスをドット区切りに変換して、Documentsオブジェクトからプロパティを取得
        // 例: "models/dalle3" -> Documents[lang]["models"]["dalle3"]
        const pathParts = documentPath.split("/");

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let documentObj: any = Documents[lang as "en" | "ja"];
        for (const part of pathParts) {
          if (!documentObj) break;

          // ケバブケースをキャメルケースに変換 (例: model-info -> modelInfo)
          const camelCasePart = part.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
          documentObj = documentObj[camelCasePart as keyof typeof documentObj];
        }

        if (!documentObj || typeof documentObj !== "string") {
          throw new Error(`Document not found: ${documentPath} (${lang})`);
        }

        // URLからマークダウンファイルを取得
        const response = await fetch(documentObj as string);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const text = await response.text();

        // マークダウンをHTMLに変換
        const html = marked(text);
        setDocument(html);

        // タイトルを抽出 (最初の見出し行)
        const titleMatch = text.match(/^#\s+(.+)$/m);
        setTitle(titleMatch ? titleMatch[1] : "Document");
      } catch (error) {
        console.error("Error loading document:", error instanceof Error ? error.message : "Unknown error");
        setError(error instanceof Error ? error.message : "Unknown error");
        setDocument("");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [documentPath, customLang]);

  return { document, isLoading, error, title };
};
